"use client";

import { useState } from "react";
import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  WASHER_LOADER_TYPES,
  WASHING_MACHINE_LOAD_KG_MAX,
  WASHING_MACHINE_LOAD_KG_MIN,
  WASHING_MACHINE_LOAD_KG_PRESETS,
  type WasherLoaderType,
  type WashingMachineSpecFormState,
} from "@/lib/listing/washingMachineListingSpec";
import {
  APPLIANCE_INVERTER_TYPES,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import { cn } from "@/lib/utils";

interface WashingMachineSpecFieldsProps {
  value: WashingMachineSpecFormState;
  onChange: (next: WashingMachineSpecFormState) => void;
  className?: string;
}

function formatInverterLabel(value: ApplianceInverterType): string {
  return value === "inverter" ? "Inverter" : "Non-inverter";
}

function formatKgPreset(kg: number): string {
  return `${kg} kg`;
}

function formatLoaderLabel(value: WasherLoaderType): string {
  return value === "top_loader" ? "Top load" : "Front load";
}

/**
 * Step 2 panel for washing machine (canonical `washing-machine`).
 * Load capacity + loader type + inverter required.
 */
export default function WashingMachineSpecFields({
  value,
  onChange,
  className = "",
}: WashingMachineSpecFieldsProps) {
  const [customKg, setCustomKg] = useState("");

  const applyCustomKg = () => {
    const parsed = Number.parseFloat(customKg);
    if (!Number.isFinite(parsed)) return;
    if (
      parsed < WASHING_MACHINE_LOAD_KG_MIN ||
      parsed > WASHING_MACHINE_LOAD_KG_MAX
    ) {
      return;
    }
    onChange({ ...value, loadCapacityKg: parsed });
    setCustomKg("");
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Load capacity (kg) <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...WASHING_MACHINE_LOAD_KG_PRESETS]}
          selected={
            typeof value.loadCapacityKg === "number" &&
            (WASHING_MACHINE_LOAD_KG_PRESETS as readonly number[]).includes(
              value.loadCapacityKg,
            )
              ? value.loadCapacityKg
              : ""
          }
          onSelect={(loadCapacityKg: number) =>
            onChange({ ...value, loadCapacityKg })
          }
          formatLabel={formatKgPreset}
        />
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input
            type="number"
            inputMode="decimal"
            min={WASHING_MACHINE_LOAD_KG_MIN}
            max={WASHING_MACHINE_LOAD_KG_MAX}
            step={0.5}
            placeholder="Other kg"
            value={
              customKg !== ""
                ? customKg
                : typeof value.loadCapacityKg === "number" &&
                    !(
                      WASHING_MACHINE_LOAD_KG_PRESETS as readonly number[]
                    ).includes(value.loadCapacityKg)
                  ? String(value.loadCapacityKg)
                  : ""
            }
            onChange={(e) => setCustomKg(e.target.value)}
            onBlur={applyCustomKg}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyCustomKg();
              }
            }}
            className="w-28 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            aria-label="Custom load capacity in kg"
          />
          <span className="text-xs text-text-light">
            Or type a value ({WASHING_MACHINE_LOAD_KG_MIN}–
            {WASHING_MACHINE_LOAD_KG_MAX})
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Top or front load? <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...WASHER_LOADER_TYPES]}
          selected={value.washerLoaderType}
          onSelect={(washerLoaderType: WasherLoaderType) =>
            onChange({ ...value, washerLoaderType })
          }
          formatLabel={formatLoaderLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Inverter type <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...APPLIANCE_INVERTER_TYPES]}
          selected={value.applianceInverterType}
          onSelect={(applianceInverterType: ApplianceInverterType) =>
            onChange({ ...value, applianceInverterType })
          }
          formatLabel={formatInverterLabel}
        />
      </div>
    </div>
  );
}
